import React, { useState, useEffect } from 'react';

interface FoodWheelProps {
    isOpen: boolean;
    onClose: () => void;
}

const FoodWheel: React.FC<FoodWheelProps> = ({ isOpen, onClose }) => {
    const [isSpinning, setIsSpinning] = useState(false);
    const [selectedFood, setSelectedFood] = useState<string | null>(null);
    const [currentDisplay, setCurrentDisplay] = useState<string>('');
    const [foods, setFoods] = useState([
        'Cơm gà',
        'Bún bò',
        'Mì quảng',
        'KFC',
        'Jollibee',
        'Dooki',
        'Bún đậu',
        'Bún mắm',
        'Bánh xèo'
    ]);
    const [newFood, setNewFood] = useState('');
    const [showFoodList, setShowFoodList] = useState(false);

    const startRandomSelection = () => {
        if (isSpinning || foods.length === 0) return;

        setIsSpinning(true);
        setSelectedFood(null);
        setCurrentDisplay('');

        // Random display interval
        const interval = setInterval(() => {
            const randomFood = foods[Math.floor(Math.random() * foods.length)];
            setCurrentDisplay(randomFood);
        }, 100);

        // Stop after 5 seconds and select final food
        setTimeout(() => {
            clearInterval(interval);
            const finalFood = foods[Math.floor(Math.random() * foods.length)];
            setSelectedFood(finalFood);
            setCurrentDisplay(finalFood);
            setIsSpinning(false);
        }, 5000);
    };

    const addFood = () => {
        if (newFood.trim() !== '' && !foods.includes(newFood.trim())) {
            setFoods([...foods, newFood.trim()]);
            setNewFood('');
        }
    };

    const removeFood = (index: number) => {
        setFoods(foods.filter((_, i) => i !== index));
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            addFood();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-pink-600 mb-6">
                        🍽️ Hôm nay ăn gì? 🍽️
                    </h2>

                    {/* Food List Management */}
                    <div className="mb-6">
                        <div className="flex justify-center mb-4">
                            <button
                                onClick={() => setShowFoodList(!showFoodList)}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 text-sm"
                            >
                                {showFoodList ? 'Ẩn danh sách' : 'Sửa danh sách món ăn'}
                            </button>
                        </div>

                        {showFoodList && (
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                                <div className="flex gap-2 mb-4">
                                    <input
                                        type="text"
                                        value={newFood}
                                        onChange={(e) => setNewFood(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Nhập món ăn mới"
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                                    />
                                    <button
                                        onClick={addFood}
                                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
                                    >
                                        Thêm
                                    </button>
                                </div>

                                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                                    {foods.map((food, index) => (
                                        <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                                            <span className="text-sm truncate">{food}</span>
                                            <button
                                                onClick={() => removeFood(index)}
                                                className="ml-2 text-red-500 hover:text-red-700 text-sm"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Food Display */}
                    <div className="relative w-64 h-64 mx-auto mb-6">
                        <div className="w-full h-full bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center border-4 border-pink-200 shadow-lg">
                            <div className="text-center">
                                {isSpinning ? (
                                    <div className="space-y-2">
                                        <div className="text-4xl mb-2">🎲</div>
                                        <div className="text-2xl font-bold text-pink-600 animate-pulse">
                                            {currentDisplay || 'Đang chọn...'}
                                        </div>
                                        <div className="text-sm text-gray-600">
                                            Đang random...
                                        </div>
                                    </div>
                                ) : selectedFood ? (
                                    <div className="space-y-2">
                                        <div className="text-4xl mb-2">🎉</div>
                                        <div className="text-2xl font-bold text-pink-600">
                                            {selectedFood}
                                        </div>
                                        <div className="text-sm text-gray-600">
                                            Món ăn được chọn!
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <div className="text-4xl mb-2">🍽️</div>
                                        <div className="text-lg text-gray-600">
                                            Nhấn nút để bắt đầu
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Decorative elements */}
                        <div className="absolute top-4 left-4 text-2xl">🍕</div>
                        <div className="absolute top-4 right-4 text-2xl">🍜</div>
                        <div className="absolute bottom-4 left-4 text-2xl">🍔</div>
                        <div className="absolute bottom-4 right-4 text-2xl">🍣</div>
                    </div>

                    {/* Result */}
                    {selectedFood && !isSpinning && (
                        <div className="mb-6 p-4 bg-gradient-to-r from-pink-100 to-purple-100 rounded-lg border border-pink-200">
                            <p className="text-lg font-semibold text-pink-800">
                                🎉 Hôm nay ăn: <span className="text-pink-600 font-bold">{selectedFood}</span> 🎉
                            </p>
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex justify-center space-x-4">
                        <button
                            onClick={startRandomSelection}
                            disabled={isSpinning}
                            className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg hover:from-pink-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg"
                        >
                            {isSpinning ? 'Đang chọn...' : 'Bắt đầu chọn'}
                        </button>
                        <button
                            onClick={onClose}
                            className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 font-medium"
                        >
                            Đóng
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FoodWheel; 